// src/PremiumPage.jsx
import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';

const GOLD = '#d4a550';
const GOLD_DIM = 'rgba(212,165,80,0.12)';
const GOLD_BORDER = 'rgba(212,165,80,0.35)';
const SURFACE = '#0f0f16';
const BORDER = 'rgba(255,255,255,0.07)';
const TEXT = '#f0ece4';
const TEXT_DIM = '#8a8a9a';
const FAINT = '#2a2a38';

// Struttura piani: solo dati strutturali, le label vengono dalla traduzione
const PLANS = [
  {
    id: 'starter', name: 'Starter',
    color: '#60a5fa', colorDim: 'rgba(96,165,250,0.12)', colorBorder: 'rgba(96,165,250,0.35)',
    price: 199, priceAnnual: 166, discountPct: 17,
    features: [
      { ok: true },{ ok: true },{ ok: false },{ ok: false },{ ok: true },
      { ok: true },{ ok: true },{ ok: true },{ ok: false },{ ok: true },
      { ok: false },{ ok: true },{ ok: true },{ ok: false },
    ],
  },
  {
    id: 'plus', name: 'Plus',
    color: GOLD, colorDim: GOLD_DIM, colorBorder: GOLD_BORDER,
    price: 499, priceAnnual: 416, discountPct: 17, featured: true,
    features: [
      { ok: true, useKey: 'label_plus' },{ ok: true },{ ok: true },{ ok: true },{ ok: true },
      { ok: true },{ ok: true },{ ok: true },{ ok: true, useKey: 'label_plus' },{ ok: true },
      { ok: false },{ ok: true, useKey: 'label_plus', note: true },{ ok: true },{ ok: false },
    ],
  },
  {
    id: 'pro', name: 'Pro',
    color: '#a78bfa', colorDim: 'rgba(167,139,250,0.12)', colorBorder: 'rgba(167,139,250,0.35)',
    price: 899, priceAnnual: 833, discountPct: 7,
    features: [
      { ok: true, useKey: 'label_pro' },{ ok: true },{ ok: true },{ ok: true },{ ok: true },
      { ok: true },{ ok: true },{ ok: true },{ ok: true, useKey: 'label_pro' },{ ok: true },
      { ok: true },{ ok: true, useKey: 'label_pro', note: true },{ ok: true },{ ok: true },
    ],
  },
  {
    id: 'unlimited', name: 'Unlimited',
    color: '#4ade80', colorDim: 'rgba(74,222,128,0.12)', colorBorder: 'rgba(74,222,128,0.35)',
    price: 1499, priceAnnual: 1333, discountPct: 11,
    features: [
      { ok: true, useKey: 'label_unl' },{ ok: true },{ ok: true },{ ok: true },{ ok: true },
      { ok: true },{ ok: true },{ ok: true },{ ok: true, useKey: 'label_unl' },{ ok: true },
      { ok: true, useKey: 'label_unl' },{ ok: true },{ ok: true, useKey: 'label_unl' },{ ok: true },
    ],
  },
];

function Countdown({ expiresAt }) {
  const { t } = useTranslation();
  const [timeLeft, setTimeLeft] = useState(null);

  useEffect(() => {
    const calc = () => {
      const diff = new Date(expiresAt) - new Date();
      if (diff <= 0) { setTimeLeft({ expired: true }); return; }
      setTimeLeft({
        days: Math.floor(diff / (1000*60*60*24)),
        hours: Math.floor((diff % (1000*60*60*24)) / (1000*60*60)),
        mins: Math.floor((diff % (1000*60*60)) / (1000*60)),
        expired: false,
      });
    };
    calc();
    const timer = setInterval(calc, 60000);
    return () => clearInterval(timer);
  }, [expiresAt]);

  if (!timeLeft) return null;

  return (
    <div style={{background:'rgba(212,165,80,0.06)',border:'1px solid rgba(212,165,80,0.2)',borderRadius:12,padding:'28px 32px',textAlign:'center',marginBottom:48}}>
      {timeLeft.expired ? (
        <>
          <div style={{fontFamily:"'Space Mono',monospace",fontSize:11,letterSpacing:'0.2em',color:GOLD,textTransform:'uppercase',marginBottom:12}}>{t('premium.trial_expired_label')}</div>
          <div style={{fontSize:18,fontWeight:700,color:TEXT,marginBottom:20}}>{t('premium.trial_expired_text')}</div>
        </>
      ) : (
        <>
          <div style={{fontFamily:"'Space Mono',monospace",fontSize:11,letterSpacing:'0.2em',color:GOLD,textTransform:'uppercase',marginBottom:16}}>{t('premium.countdown_label')}</div>
          <div style={{display:'flex',gap:16,justifyContent:'center',alignItems:'center',marginBottom:16}}>
            {[
              {val:timeLeft.days, label:t('premium.countdown_days')},
              {val:timeLeft.hours, label:t('premium.countdown_hours')},
              {val:timeLeft.mins, label:t('premium.countdown_mins')},
            ].map(({val,label}) => (
              <div key={label} style={{textAlign:'center'}}>
                <div style={{fontFamily:"'Kanit',sans-serif",fontSize:48,fontWeight:900,color:GOLD,lineHeight:1}}>{String(val).padStart(2,'0')}</div>
                <div style={{fontFamily:"'Space Mono',monospace",fontSize:9,letterSpacing:'0.15em',color:TEXT_DIM,textTransform:'uppercase',marginTop:4}}>{label}</div>
              </div>
            ))}
          </div>
          <div style={{fontFamily:"'Kanit',sans-serif",fontSize:14,fontWeight:300,color:TEXT_DIM}}>
            {t('premium.countdown_sub_pre')} <strong style={{color:TEXT}}>{t('premium.countdown_sub_trial')}</strong> {t('premium.countdown_sub_post')}
          </div>
        </>
      )}
    </div>
  );
}

export default function PremiumPage({ user }) {
  const { t } = useTranslation();
  const [billing, setBilling] = useState('monthly');
  const trialExpired = user?.trial_expires_at ? new Date(user.trial_expires_at) <= new Date() : false;
  const showPrices = trialExpired;
  const features = t('premium.features', { returnObjects: true });

  return (
    <div style={{background:'#07070a',minHeight:'100vh',color:TEXT,fontFamily:"'Kanit',sans-serif",padding:'40px 24px 80px'}}>

      <div style={{maxWidth:1100,margin:'0 auto 56px'}}>
        <div style={{fontFamily:"'Space Mono',monospace",fontSize:11,letterSpacing:'0.25em',textTransform:'uppercase',color:GOLD,marginBottom:16}}>{t('premium.eyebrow')}</div>
        <h1 style={{fontSize:'clamp(36px,5vw,64px)',fontWeight:900,letterSpacing:'-0.02em',textTransform:'uppercase',lineHeight:0.95,marginBottom:20}}>
          {t('premium.h1_line1')}<br/><span style={{color:GOLD}}>{t('premium.h1_line2')}</span>
        </h1>
        <p style={{fontSize:17,fontWeight:300,color:TEXT_DIM,lineHeight:1.7,whiteSpace:'pre-line'}}>
          {t('premium.intro')}<br/><br/>
          <strong style={{color:TEXT}}>{t('premium.intro_strong')}</strong> {t('premium.intro_suffix')}
        </p>
      </div>

      {user?.trial_expires_at && (
        <div style={{maxWidth:1100,margin:'0 auto'}}>
          <Countdown expiresAt={user.trial_expires_at} />
        </div>
      )}

      {showPrices && (
        <div style={{maxWidth:1100,margin:'0 auto 40px',display:'flex',justifyContent:'center'}}>
          <div style={{display:'inline-flex',background:'rgba(255,255,255,0.04)',border:`1px solid ${BORDER}`,borderRadius:8,padding:4,gap:4}}>
            {[['monthly', t('premium.billing_monthly')],['annual', t('premium.billing_annual')]].map(([val,label]) => (
              <button key={val} onClick={()=>setBilling(val)}
                style={{background:billing===val?GOLD_DIM:'transparent',border:billing===val?`1px solid ${GOLD_BORDER}`:'1px solid transparent',borderRadius:6,color:billing===val?GOLD:TEXT_DIM,fontFamily:"'Kanit',sans-serif",fontSize:13,fontWeight:700,padding:'8px 24px',cursor:'pointer',transition:'all 0.2s'}}>
                {label}
                {val==='annual' && <span style={{marginLeft:8,fontFamily:"'Space Mono',monospace",fontSize:9,color:'#4ade80'}}>{t('premium.billing_save')}</span>}
              </button>
            ))}
          </div>
        </div>
      )}

      <div style={{maxWidth:1100,margin:'0 auto',display:'grid',gridTemplateColumns:'repeat(auto-fit,minmax(240px,1fr))',gap:20,marginBottom:60}}>
        {PLANS.map(plan => (
          <div key={plan.id} style={{background:plan.featured?`linear-gradient(135deg,${plan.colorDim} 0%,${SURFACE} 60%)`:SURFACE,border:`1px solid ${plan.featured?plan.colorBorder:BORDER}`,borderRadius:16,padding:'32px 28px',position:'relative',display:'flex',flexDirection:'column'}}>
            {plan.featured && (
              <div style={{position:'absolute',top:-12,left:'50%',transform:'translateX(-50%)',background:GOLD,color:'#07070a',fontFamily:"'Space Mono',monospace",fontSize:9,fontWeight:700,letterSpacing:'0.15em',padding:'4px 16px',borderRadius:20,textTransform:'uppercase',whiteSpace:'nowrap'}}>
                {t('premium.featured_badge')}
              </div>
            )}
            <div style={{marginBottom:24}}>
              <div style={{fontFamily:"'Kanit',sans-serif",fontSize:24,fontWeight:900,color:plan.color,textTransform:'uppercase',letterSpacing:'0.05em',marginBottom:6}}>{plan.name}</div>
              <div style={{fontFamily:"'Kanit',sans-serif",fontSize:13,fontWeight:300,color:TEXT_DIM}}>{t(`premium.plan_taglines.${plan.id}`)}</div>
            </div>
            <div style={{marginBottom:28,minHeight:72,display:'flex',alignItems:'center'}}>
              {showPrices ? (
                <div>
                  <div style={{display:'flex',alignItems:'baseline',gap:6}}>
                    <span style={{fontFamily:"'Kanit',sans-serif",fontSize:42,fontWeight:900,color:TEXT,lineHeight:1}}>
                      {billing==='annual'?plan.priceAnnual:plan.price}€
                    </span>
                    <span style={{fontFamily:"'Space Mono',monospace",fontSize:11,color:TEXT_DIM}}>{t('premium.per_month')}</span>
                  </div>
                  {billing==='annual' && (
                    <div style={{fontFamily:"'Space Mono',monospace",fontSize:10,color:'#4ade80',marginTop:6,letterSpacing:'0.1em'}}>
                      {t('premium.annual_discount').replace('{pct}', plan.discountPct)}
                    </div>
                  )}
                </div>
              ) : (
                <div style={{display:'flex',alignItems:'center',gap:10}}>
                  <div style={{fontFamily:"'Space Mono',monospace",fontSize:28,color:FAINT,letterSpacing:'0.1em'}}>{t('premium.price_hidden_dots')}</div>
                  <div style={{fontFamily:"'Space Mono',monospace",fontSize:9,color:TEXT_DIM,letterSpacing:'0.1em',textTransform:'uppercase',lineHeight:1.5,whiteSpace:'pre-line'}}>{t('premium.price_hidden_label')}</div>
                </div>
              )}
            </div>
            <div style={{display:'flex',flexDirection:'column',gap:10,flex:1}}>
              {plan.features.map((f, i) => {
                const feat = Array.isArray(features) ? features[i] : {};
                const label = f.useKey && feat[f.useKey] ? feat[f.useKey] : feat.label || '';
                return (
                  <div key={i} style={{display:'flex',alignItems:'flex-start',gap:10}}>
                    <span style={{fontSize:13,color:f.ok?'#4ade80':FAINT,flexShrink:0,marginTop:1}}>{f.ok?'✓':'×'}</span>
                    <span style={{fontFamily:"'Kanit',sans-serif",fontSize:13,fontWeight:f.ok?400:300,color:f.ok?TEXT:TEXT_DIM,lineHeight:1.4}}>
                      {label}
                      {f.note && <><br/><span style={{fontFamily:"'Space Mono',monospace",fontSize:9,color:GOLD}}>{t('premium.feature_note_per_prod')}</span></>}
                    </span>
                  </div>
                );
              })}
            </div>
            {showPrices && (
              <a href="/contatti" style={{display:'block',marginTop:28,padding:'13px 20px',background:plan.featured?GOLD_DIM:'rgba(255,255,255,0.04)',border:`1px solid ${plan.featured?GOLD_BORDER:BORDER}`,borderRadius:8,color:plan.featured?GOLD:TEXT_DIM,fontFamily:"'Kanit',sans-serif",fontSize:13,fontWeight:700,textAlign:'center',textDecoration:'none',letterSpacing:'0.08em',textTransform:'uppercase'}}>
                {t('premium.cta')}
              </a>
            )}
          </div>
        ))}
      </div>

      <div style={{maxWidth:1100,margin:'0 auto 60px'}}>
        <div style={{background:SURFACE,border:`1px solid ${BORDER}`,borderRadius:16,padding:'32px 40px',display:'flex',alignItems:'center',justifyContent:'space-between',flexWrap:'wrap',gap:24}}>
          <div>
            <div style={{fontFamily:"'Space Mono',monospace",fontSize:10,letterSpacing:'0.2em',color:TEXT_DIM,textTransform:'uppercase',marginBottom:8}}>{t('premium.free_label')}</div>
            <div style={{fontFamily:"'Kanit',sans-serif",fontSize:22,fontWeight:800,color:TEXT,marginBottom:8}}>{t('premium.free_title')}</div>
            <div style={{fontFamily:"'Kanit',sans-serif",fontSize:14,fontWeight:300,color:TEXT_DIM,maxWidth:500,lineHeight:1.6}}>
              {t('premium.free_desc')} <strong style={{color:TEXT}}>{t('premium.free_strong')}</strong>
            </div>
          </div>
          <div style={{fontFamily:"'Kanit',sans-serif",fontSize:42,fontWeight:900,color:GOLD}}>{t('premium.free_price')}</div>
        </div>
      </div>

      <div style={{maxWidth:1100,margin:'0 auto',textAlign:'center'}}>
        <p style={{fontFamily:"'Space Mono',monospace",fontSize:10,color:FAINT,letterSpacing:'0.1em',lineHeight:1.8,whiteSpace:'pre-line'}}>
          {t('premium.footer_note')} <a href="mailto:info@joinjkl.com" style={{color:GOLD,textDecoration:'none'}}>info@joinjkl.com</a>
        </p>
      </div>
    </div>
  );
}
